import React from 'react';
import { Delete, ScanLine } from 'lucide-react';
import { feedbackButton } from '../../utils/appFeedback';

/**
 * ScanNumericKeypad
 *
 * Always-visible, branded 4×4 numeric keypad used by the venue, staff and
 * demo scan surfaces. The right-most column is a tall "SCAN" action button
 * that spans the full height of the keypad (iPhone-calculator style).
 *
 *  - Digits 0-9, a single decimal dot and a delete key edit `value`
 *  - Dot is ignored once a '.' or ',' is already present
 *  - Max length 8 chars so bills can't overflow the amount card
 *  - Scan button is disabled until `canScan` is true; disabled state is
 *    muted but still visible so the hint "Enter amount first" is obvious.
 */
const FONT = "'Inria Serif', serif";
const MAX_LEN = 8;

const KEY_STYLE_BASE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: FONT,
  fontSize: 28,
  fontWeight: 600,
  color: 'var(--text)',
  background: 'var(--surface)',
  border: '1px solid var(--card-border)',
  borderRadius: 18,
  height: 62,
  cursor: 'pointer',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
  transition: 'transform 90ms ease, background 160ms ease, box-shadow 160ms ease',
  boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
};

function KeypadKey({ children, onPress, testId, ariaLabel, muted = false }) {
  const [pressed, setPressed] = React.useState(false);
  const handlePress = () => {
    feedbackButton();
    onPress();
  };
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={ariaLabel}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      onClick={handlePress}
      style={{
        ...KEY_STYLE_BASE,
        color: muted ? 'var(--text-2)' : 'var(--text)',
        transform: pressed ? 'scale(0.96)' : 'scale(1)',
        background: pressed
          ? 'color-mix(in srgb, var(--accent-purple) 10%, var(--surface))'
          : 'var(--surface)',
        boxShadow: pressed
          ? '0 1px 2px rgba(16,24,40,0.08) inset'
          : '0 1px 2px rgba(16,24,40,0.04)',
      }}
    >
      {children}
    </button>
  );
}

export default function ScanNumericKeypad({
  value,
  onChange,
  onScan,
  canScan,
  scanLabel = 'SCAN',
  testIdPrefix = 'scan',
}) {
  const appendDigit = (d) => {
    const current = value || '';
    if (current.length >= MAX_LEN) return;
    // Guard against leading zeros like "00" or "01"
    if (current === '0' && d !== '.' && d !== ',') {
      onChange(d);
      return;
    }
    onChange(current + d);
  };

  const appendDot = () => {
    const current = value || '';
    if (current.includes('.') || current.includes(',')) return;
    if (current.length >= MAX_LEN) return;
    onChange(current === '' ? '0.' : current + '.');
  };

  const handleDelete = () => {
    const current = value || '';
    if (!current) return;
    onChange(current.slice(0, -1));
  };

  const handleScan = () => {
    if (!canScan) return;
    feedbackButton();
    onScan();
  };

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return (
    <div
      className="scan-keypad"
      data-testid={`${testIdPrefix}-keypad`}
      style={{
        width: 'calc(100% - 32px)',
        maxWidth: 360,
        marginTop: 20,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr) 1.1fr',
        gridTemplateRows: 'repeat(4, auto)',
        gap: 10,
      }}
    >
      {digits.map((d) => (
        <KeypadKey
          key={d}
          onPress={() => appendDigit(d)}
          testId={`${testIdPrefix}-keypad-${d}`}
          ariaLabel={`Enter digit ${d}`}
        >
          {d}
        </KeypadKey>
      ))}

      {/* Bottom row: . 0 ⌫ */}
      <KeypadKey
        onPress={appendDot}
        testId={`${testIdPrefix}-keypad-dot`}
        ariaLabel="Decimal separator"
        muted
      >
        .
      </KeypadKey>
      <KeypadKey
        onPress={() => appendDigit('0')}
        testId={`${testIdPrefix}-keypad-0`}
        ariaLabel="Enter digit 0"
      >
        0
      </KeypadKey>
      <KeypadKey
        onPress={handleDelete}
        testId={`${testIdPrefix}-keypad-delete`}
        ariaLabel="Delete last digit"
        muted
      >
        <Delete size={22} strokeWidth={1.8} />
      </KeypadKey>

      {/* Scan button — spans the full right column */}
      <button
        type="button"
        data-testid={`${testIdPrefix}-keypad-scan`}
        aria-label="Open camera to scan QR"
        onClick={handleScan}
        disabled={!canScan}
        style={{
          gridColumn: '4 / 5',
          gridRow: '1 / span 4',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          borderRadius: 22,
          border: 'none',
          cursor: canScan ? 'pointer' : 'not-allowed',
          color: '#FFFFFF',
          fontFamily: FONT,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: 1.6,
          background: canScan
            ? 'linear-gradient(160deg, var(--accent-purple) 0%, var(--accent-purple-strong) 100%)'
            : 'color-mix(in srgb, var(--accent-purple) 22%, var(--surface-2))',
          boxShadow: canScan
            ? '0 10px 24px -10px color-mix(in srgb, var(--accent-purple) 55%, transparent), inset 0 1px 0 rgba(255,255,255,0.18)'
            : 'inset 0 1px 0 rgba(255,255,255,0.08)',
          opacity: canScan ? 1 : 0.55,
          transition: 'transform 120ms ease, opacity 180ms ease, box-shadow 180ms ease',
          WebkitTapHighlightColor: 'transparent',
          padding: '12px 8px',
        }}
        onPointerDown={(e) => { if (canScan) e.currentTarget.style.transform = 'scale(0.97)'; }}
        onPointerUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        onPointerLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <ScanLine size={34} strokeWidth={1.8} />
        <span>{scanLabel}</span>
        {!canScan && (
          <span
            data-testid={`${testIdPrefix}-keypad-scan-hint`}
            style={{
              fontSize: 9,
              fontWeight: 600,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              opacity: 0.85,
              textAlign: 'center',
              lineHeight: 1.3,
              padding: '0 6px',
            }}
          >
            Enter amount
          </span>
        )}
      </button>
    </div>
  );
}
